import React, { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { usePushNotification } from "src/hooks/usePushNotification";

const PushNotificationPrompt: React.FC = () => {
  const {
    shouldShowPrompt,
    isEnabling,
    enableNotifications,
    dismissPrompt,
    permissionState,
  } = usePushNotification();

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const handleAnimateOut = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
    }, 400);
  };

  if (!shouldShowPrompt && isVisible) {
    setIsVisible(false);
  }

  useEffect(() => {
    if (shouldShowPrompt) {
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowPrompt]);

  useEffect(() => {
    if (permissionState === "granted") {
      setTimeout(() => {
        handleAnimateOut();
      }, 0);
    }
  }, [permissionState]);

  const handleEnable = async () => {
    const success = await enableNotifications();
    if (success) {
      handleAnimateOut();
    }
  };

  const handleDismiss = () => {
    dismissPrompt();
    handleAnimateOut();
  };

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes slideOutDown {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
        }
        .animate-slide-in {
          animation: slideInUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        .animate-slide-out {
          animation: slideOutDown 0.3s cubic-bezier(0.19, 1, 0.22, 1) forwards;
        }
      `}</style>

      <div
        className={`fixed bottom-6 right-6 z-[10000] max-w-[340px] w-full ${
          isAnimatingOut ? "animate-slide-out" : "animate-slide-in"
        }`}
      >
        <div className="relative p-5 bg-white/95 dark:bg-[#0D0E12]/85 backdrop-blur-3xl rounded-2xl border border-gray-100 dark:border-white/5 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.1)] dark:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.7)] overflow-hidden transition-all duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/[0.08] rounded-full filter blur-2xl z-0" />

          <div className="relative flex items-start gap-4 z-10">
            <div className="relative flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#066766] to-[#0FA243] flex items-center justify-center shadow-[0_4px_12px_rgba(15,162,67,0.3)]">
              <Bell className="w-5.5 h-5.5 text-white" />
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-[#0D0E12]" />
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-[#0D0E12] animate-ping opacity-75" />
            </div>

            <div className="flex-1 min-w-0 pr-2">
              <h4 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                Enable Notifications
              </h4>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                Get real-time stock and market alerts.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 z-10 relative">
            <button
              onClick={handleEnable}
              disabled={isEnabling}
              className="flex-1 py-2 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-[#066766] to-[#0FA243] hover:opacity-95 hover:shadow-[0_4px_12px_rgba(15,162,67,0.3)] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              {isEnabling ? "Enabling…" : "Enable"}
            </button>
            <button
              onClick={handleDismiss}
              className="py-2 px-3 rounded-xl text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-all cursor-pointer"
            >
              Later
            </button>
          </div>

          {isEnabling && (
            <div className="mt-2 text-center z-10 relative animate-pulse">
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">
                Please click &quot;Allow&quot; in the browser prompt above ↗
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PushNotificationPrompt;
